/**
 * One note on the board: who said it (in their own colour, with their name),
 * when, and what. Plus the two things you can do to it, when you're allowed:
 *
 *   edit   — the author only. Nobody rewrites someone else's words.
 *   delete — the author, or any adult (a parent has to be able to take a post down).
 *
 * The buttons are a courtesy; worker/routes/messages.ts is what enforces this.
 * Deleting asks first, inline — no window.confirm, and no accidental one-tap loss.
 */
import { useState, type KeyboardEvent } from 'react';
import type { Identity, Message } from '../../shared/types';
import {
  Avatar,
  Banner,
  authorColor,
  authorName,
  btnPrimary,
  btnQuiet,
  btnSecondary,
  cx,
  fullTime,
  relativeTime,
  textArea,
} from './shared';
import { canDelete, canEdit } from './useBoard';

export default function MessageItem({
  message,
  me,
  now,
  onEdit,
  onDelete,
}: {
  message: Message;
  me: Identity;
  now: number;
  onEdit: (id: number, body: string) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'confirm'>('view');
  const [draft, setDraft] = useState(message.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = authorName(message.author);
  const color = authorColor(message.author);
  const mine = canEdit(message, me);
  const removable = canDelete(message, me);

  async function saveEdit() {
    const text = draft.trim();
    if (!text) {
      setError('A message can’t be empty. Delete it instead if you want it gone.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onEdit(message.id, text);
      setMode('view');
    } catch (err) {
      // The edited text stays in the box — same rule as the composer.
      setError(err instanceof Error ? err.message : 'That edit didn’t save.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError(null);
    try {
      await onDelete(message.id);
      // The row disappears from the list on success; no state to reset.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That message wasn’t removed.');
      setBusy(false);
      setMode('view');
    }
  }

  function onEditKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void saveEdit();
    }
    if (e.key === 'Escape') {
      setDraft(message.body);
      setError(null);
      setMode('view');
    }
  }

  return (
    <li className="flex gap-3">
      <Avatar name={name} color={color} />

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: color ?? 'var(--color-ink-faint)' }}
            />
            {name}
            {mine && <span className="text-xs font-normal text-ink-faint">(you)</span>}
          </span>
          <time
            dateTime={message.created_at}
            title={fullTime(message.created_at)}
            className="text-xs text-ink-faint"
          >
            {relativeTime(message.created_at, now)}
          </time>
          {message.edited_at && (
            <span
              className="text-xs text-ink-faint"
              title={`Edited ${fullTime(message.edited_at)}`}
            >
              · edited
            </span>
          )}
        </p>

        {mode === 'edit' ? (
          <div className="mt-2 space-y-2">
            <label htmlFor={`edit-${message.id}`} className="sr-only">
              Edit your message
            </label>
            <textarea
              id={`edit-${message.id}`}
              rows={3}
              autoFocus
              value={draft}
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onEditKeyDown}
              className={textArea}
            />
            {error && <Banner tone="error">{error}</Banner>}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void saveEdit()}
                disabled={busy}
                className={btnPrimary}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setDraft(message.body);
                  setError(null);
                  setMode('view');
                }}
                className={btnSecondary}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mt-1 text-base leading-relaxed break-words whitespace-pre-wrap text-ink">
              {message.body}
            </p>

            {error && (
              <div className="mt-2">
                <Banner tone="error">{error}</Banner>
              </div>
            )}

            {mode === 'confirm' ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-sm text-ink-soft">Take this off the board?</span>
                <button
                  type="button"
                  onClick={() => void confirmDelete()}
                  disabled={busy}
                  className={cx(btnSecondary, 'border-over/50 text-over hover:bg-clay-soft')}
                >
                  {busy ? 'Removing…' : 'Yes, remove it'}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('view')}
                  disabled={busy}
                  className={btnQuiet}
                >
                  Keep it
                </button>
              </div>
            ) : (
              (mine || removable) && (
                <div className="mt-1 -ml-3 flex flex-wrap">
                  {mine && (
                    <button type="button" onClick={() => setMode('edit')} className={btnQuiet}>
                      Edit
                    </button>
                  )}
                  {removable && (
                    <button type="button" onClick={() => setMode('confirm')} className={btnQuiet}>
                      {mine ? 'Delete' : 'Remove'}
                      <span className="sr-only"> the message from {name}</span>
                    </button>
                  )}
                </div>
              )
            )}
          </>
        )}
      </div>
    </li>
  );
}

/** A message that is still in flight. It is visibly NOT on the board yet. */
export function PendingItem({ me, body }: { me: Identity; body: string }) {
  return (
    <li className="flex gap-3 opacity-70">
      <Avatar name={me.name ?? 'You'} color={me.color} />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold text-ink">{me.name ?? 'You'}</span>
          <span className="text-xs text-ink-faint">Sending…</span>
        </p>
        <p className="mt-1 text-base leading-relaxed break-words whitespace-pre-wrap text-ink-soft">
          {body}
        </p>
      </div>
    </li>
  );
}
